import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, ClipboardCheck,
  GraduationCap, FileText, History, LayoutGrid, Users, Activity, Banknote, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ICON = {
  licensure:     ShieldCheck,
  background:    ClipboardCheck,
  training:      GraduationCap,
  documentation: FileText,
  audit:         History,
};

const GRADE_COLOR = {
  A: 'text-emerald-600 border-emerald-200 bg-emerald-50',
  B: 'text-emerald-600 border-emerald-200 bg-emerald-50',
  C: 'text-amber-600 border-amber-200 bg-amber-50',
  D: 'text-amber-700 border-amber-300 bg-amber-100',
  F: 'text-red-600 border-red-200 bg-red-50',
};

const BAND_COLOR = {
  ready:    'text-emerald-700 bg-emerald-50 border-emerald-200',
  at_risk:  'text-amber-700 bg-amber-50 border-amber-200',
  blocker:  'text-red-700 bg-red-50 border-red-200',
};

const BAND_LABEL = {
  ready: 'Survey-ready',
  at_risk: 'At risk',
  blocker: 'Survey blocker',
};

/**
 * /compliance/survey-readiness — Blueprint Decision #15.
 *
 * Real-time 0-100 score with per-category drill-down. The screen
 * compliance officers screenshot before survey day.
 */
export default function SurveyReadiness() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance/survey-readiness`, getAuthHeader());
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load survey readiness');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const navItems = [
    { icon: LayoutGrid, label: 'Compliance · Survey-ready', active: true },
    { icon: ShieldCheck, label: 'Trust Portal',  href: '/trust' },
    { icon: Activity,    label: 'Executive',     href: '/operations/executive' },
    { icon: Users,       label: 'Workforce',     href: '/agency/find-clinicians' },
    { icon: Banknote,    label: 'Revenue Cycle', href: '/clinician/billing' },
    { icon: Building2,   label: 'Agency',        href: '/agency/dashboard' },
  ];

  const overall = data?.overall_score ?? 0;
  const band = data?.band || 'blocker';

  // Circular gauge geometry
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (overall / 100) * circumference;

  return (
    <DashboardShell
      navItems={navItems}
      title="Survey Readiness"
      subtitle="Real-time compliance posture · the screen you show on survey day."
      testId="survey-readiness-page"
      rightSlot={
        <Button
          onClick={load}
          disabled={loading}
          className="cn-btn cn-btn-ghost !py-2.5"
          data-testid="survey-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      }
    >
      {loading && !data ? (
        <div className="cn-card h-72 animate-pulse bg-cn-bg/60" />
      ) : (
        <>
          {/* HERO GAUGE */}
          <div className="cn-card p-8 flex flex-col lg:flex-row items-start lg:items-center gap-8" data-testid="survey-hero">
            <div className="relative w-44 h-44 shrink-0">
              <svg viewBox="0 0 160 160" className="-rotate-90 w-full h-full">
                <circle cx="80" cy="80" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="14" />
                <circle
                  cx="80" cy="80" r={radius} fill="none"
                  stroke={band === 'ready' ? '#059669' : band === 'at_risk' ? '#D97706' : '#DC2626'}
                  strokeWidth="14"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  className="transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display font-bold text-5xl text-cn-text" data-testid="survey-overall-score">
                  {overall}
                </div>
                <div className="text-xs uppercase tracking-wider font-bold text-cn-text-muted mt-1">
                  out of 100
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${BAND_COLOR[band]}`}
                data-testid="survey-band-pill"
              >
                {band === 'ready' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                {BAND_LABEL[band]}
              </span>
              <h2 className="mt-3 font-display font-bold text-2xl text-cn-text dark:text-white">
                Grade {data?.grade || '—'}
              </h2>
              <p className="mt-2 text-sm text-cn-text-muted leading-relaxed max-w-xl">
                {band === 'ready'  && 'Your network is survey-ready today. Maintain current pace.'}
                {band === 'at_risk' && 'A handful of items would block a survey if it landed today. See category drivers below.'}
                {band === 'blocker' && 'The current posture would NOT pass a survey. Address the top drivers immediately.'}
              </p>
              <p className="mt-3 text-xs text-cn-text-muted/80" data-testid="survey-scope">
                Scope: <strong>{data?.clinicians_in_scope ?? 0}</strong> clinician{(data?.clinicians_in_scope ?? 0) !== 1 ? 's' : ''} in your network
              </p>
            </div>
          </div>

          {/* CATEGORY GRID */}
          <div className="grid lg:grid-cols-2 gap-4 mt-6" data-testid="survey-categories">
            {(data?.categories || []).map((cat) => {
              const Icon = CAT_ICON[cat.id] || ShieldCheck;
              return (
                <div key={cat.id} className="cn-card p-6" data-testid={`survey-cat-${cat.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </span>
                      <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">{cat.name}</h3>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border ${GRADE_COLOR[cat.grade] || GRADE_COLOR.F}`}
                      data-testid={`survey-cat-${cat.id}-grade`}
                    >
                      {cat.grade}
                    </span>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-end justify-between mb-1">
                      <span className="text-xs text-cn-text-muted">Score</span>
                      <span className="font-display font-bold text-cn-text" data-testid={`survey-cat-${cat.id}-score`}>
                        {cat.score} / 100
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-cn-bone-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${cat.score >= 85 ? 'bg-emerald-500' : cat.score >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${cat.score}%` }}
                      />
                    </div>
                  </div>
                  {cat.drivers?.length > 0 ? (
                    <ul className="space-y-1.5" data-testid={`survey-cat-${cat.id}-drivers`}>
                      {cat.drivers.map((d, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-cn-text-muted">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      No outstanding items.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-cn-text-muted mt-6" data-testid="survey-generated-at">
            Generated at {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} ·
            Weighting: equal across five categories · CMS Conditions of Participation aligned
          </p>
        </>
      )}
    </DashboardShell>
  );
}
